/**
 * useVideoClipping Hook
 *
 * Saves a batch of marked-out clips against a source video: one API call to create the
 * records, then a best-effort pass to upload a poster frame for each.
 */

import { useCallback, useState } from 'react';
import * as prospectMediaService from '../services/prospect-media';
import { captureClipPosterFrames } from '../utils/video-clip-thumbnail';
import type { CreateVideoClipsResponse, PendingClip } from '../types/prospect-media';

export interface SaveClipsResult {
  success: boolean;
  clipCount: number;
  error?: string;
  /** True when the clips saved but none of their poster frames could be captured/uploaded. */
  thumbnailsFailed?: boolean;
}

export function useVideoClipping(prospectId: string) {
  const [isSaving, setIsSaving] = useState(false);

  const saveClips = useCallback(
    async (
      sourceVideoId: string,
      sourceVideoUrl: string,
      clips: PendingClip[]
    ): Promise<SaveClipsResult> => {
      if (clips.length === 0) {
        return { success: false, clipCount: 0, error: 'Add at least one clip before saving.' };
      }

      setIsSaving(true);
      try {
        // The clip records are what matter — create them first so a thumbnail problem can
        // never cost the user the clips they marked out.
        const response = await prospectMediaService.createVideoClips(prospectId, sourceVideoId, {
          clips: clips.map((clip) => ({
            title: clip.title.trim() || undefined,
            startSeconds: clip.startSeconds,
            endSeconds: clip.endSeconds,
            // Local id doubles as the idempotency key: a retried save resolves to the same
            // rows instead of creating a second copy of every clip.
            clientGeneratedId: clip.id,
          })),
        });

        const thumbnailsFailed = await uploadPosterFrames(sourceVideoUrl, response);

        return { success: true, clipCount: response.clips.length, thumbnailsFailed };
      } catch (err) {
        console.error('[VideoClipping] Failed to save clips', {
          error: err,
          prospectId,
          sourceVideoId,
          clipCount: clips.length,
        });
        return {
          success: false,
          clipCount: 0,
          error: err instanceof Error ? err.message : 'Failed to save clips. Please try again.',
        };
      } finally {
        setIsSaving(false);
      }
    },
    [prospectId]
  );

  return { saveClips, isSaving };
}

/**
 * Captures and uploads a poster frame per created clip.
 * Returns true when every frame failed — the caller surfaces that as a soft warning,
 * never as a failed save.
 */
async function uploadPosterFrames(
  sourceVideoUrl: string,
  response: CreateVideoClipsResponse
): Promise<boolean> {
  const targets = response.clips.filter((clip) => clip.thumbnailUploadUrl);
  if (targets.length === 0) return response.clips.length > 0;

  try {
    const frames = await captureClipPosterFrames(
      sourceVideoUrl,
      targets.map((clip) => clip.startSeconds)
    );

    const uploads = await Promise.all(
      targets.map(async (clip, index) => {
        const frame = frames[index];
        if (!frame) return false;

        const result = await prospectMediaService.uploadThumbnailToGCS(
          clip.thumbnailUploadUrl,
          frame
        );
        return result.success;
      })
    );

    return uploads.every((ok) => !ok);
  } catch (error) {
    console.warn('[VideoClipping] Poster frames unavailable; clips saved without them', {
      error,
    });
    return true;
  }
}
